import { useState, useCallback, useEffect } from 'react';
import {
  getDocuments,
  getAccounts,
  switchAccount,
  requestCredentials,
  authDocuments,
  PatientFile,
  PatientAccount,
} from '../services/documentsService';
import { useAuth } from './useAuth';

export const useDocuments = () => {
  const { isMatched } = useAuth();
  const [files, setFiles] = useState<PatientFile[]>([]);
  const [accounts, setAccounts] = useState<{ parent: PatientAccount; children: PatientAccount[] } | null>(null);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountAuthStatus, setAccountAuthStatus] = useState<Record<string, boolean>>({});

  const loadDocuments = useCallback(async () => {
    if (!isMatched) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDocuments();
      setFiles(data.files || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Ошибка загрузки документов');
    } finally {
      setIsLoading(false);
    }
  }, [isMatched]);

  const loadAccounts = useCallback(async () => {
    try {
      const data = await getAccounts();
      setAccounts({ parent: data.parent, children: data.children });
      setActiveAccountId(data.activeAccountId);

      // Store authorization status for all accounts
      const authStatus: Record<string, boolean> = {};
      authStatus[data.parent.id] = data.parent.isAuthorized;
      data.children.forEach((child) => {
        authStatus[child.id] = child.isAuthorized;
      });
      setAccountAuthStatus(authStatus);
    } catch (err: any) {
      // не критично
    }
  }, []);

  useEffect(() => {
    if (isMatched) {
      loadDocuments();
      loadAccounts();
    }
  }, [isMatched, loadDocuments, loadAccounts]);

  const handleSwitchAccount = useCallback(async (accountId: string) => {
    try {
      await switchAccount(accountId);
      setActiveAccountId(accountId);
      await loadDocuments();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  }, [loadDocuments]);

  const handleRequestCredentials = useCallback(async (type: 'sms' | 'email') => {
    try {
      const result = await requestCredentials(type);
      return { success: true, message: result.message };
    } catch (err: any) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  }, []);

  const handleAuthDocuments = useCallback(async (login: string, password: string, accountId?: string) => {
    try {
      const result = await authDocuments(login, password, accountId);
      if (result.success) {
        await loadDocuments();
      }
      return result;
    } catch (err: any) {
      return { success: false, error: err.response?.data?.message || err.message };
    }
  }, [loadDocuments]);

  return {
    files,
    accounts,
    activeAccountId,
    isLoading,
    error,
    accountAuthStatus,
    refetch: loadDocuments,
    handleSwitchAccount,
    handleRequestCredentials,
    handleAuthDocuments,
  };
};
